import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { LIST_TITLE_MAX_LENGTH } from '@molt/shared';
import { Button, ListPanel, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useCreateList } from '@/lib/lists';

/**
 * The list composer — chunk `C2`, agent B (`FE-11.1`, `FR-BOARD-009`).
 * Signatures fixed by `docs/design/c2/C2-BRIEF.md` §6.3.
 *
 * ── AC1 is the item's whole point ─────────────────────────────────────────
 * Submitting leaves the composer OPEN AND EMPTY with focus retained, "so
 * successive lists can be typed without re-opening it" — so `handleSubmit`
 * clears `title` and refocuses the field itself, in the same tick as the
 * (optimistic) mutation call, rather than waiting on a response. A composer
 * that closes on submit would satisfy every other criterion in the
 * requirement and fail the one it was written for.
 *
 * ── Two shapes, one component ─────────────────────────────────────────────
 * `trigger='button'` is the composer at the right end of the strip: closed,
 * it is the `Add a list` button (§7.1 — that exact accessible name, not
 * "Add another list"); open, it is the form. `trigger='none'` is a gap
 * composer — BoardFace renders the gap element itself (the double-click
 * target) and this component contributes nothing when closed, only the form
 * once `open` is set. `open` is fully controlled: BoardFace owns which one
 * composer is open, so this component never decides to open itself.
 *
 * ── Focus is imperative, not `autoFocus` ──────────────────────────────────
 * The same `inputRef.current?.focus()` call has to fire twice — once when
 * the composer opens (from any of its three entry points: the trigger
 * button, a gap double-click, or `ListHeader`'s "Add a list to the right"
 * keyboard equivalent) and again after every submit while it stays mounted.
 * `autoFocus` only ever covers the first, so both are done the same way, in
 * one place, rather than mixing a JSX prop with an imperative call for what
 * is the same requirement twice.
 *
 * ── The client never computes a rank (brief §3.1) ─────────────────────────
 * `previousListId`/`nextListId` pass straight through to `useCreateList` —
 * this file synthesises no `position` and imports nothing from `./rank`.
 *
 * ── Routed at `FE-80.1` ───────────────────────────────────────────────────
 * This file had FIVE English strings and ZERO `t(` call sites while sitting on
 * every board, which is how the undo slice's Spanish capture found it: `Add a
 * list` rendered in English at the end of an otherwise Spanish strip. Its
 * announcement was `` announce(`List ${trimmedTitle} added.`) `` — the same
 * interpolated-sentence shape that slice had just removed from `lib/undo.ts`,
 * and it is now a slot (`boardFace.list.added`) so a translator receives one
 * sentence with the name movable inside it.
 *
 * ⚠ `Add a list` is `boardFace.list.add`, DECLARED ONCE and shared with
 * `BoardStates.tsx`'s `BoardEmpty` fallback button, which renders the same
 * copy for the same affordance. `list-composer.routing.test.ts` pins both call
 * sites to the one key.
 */
export interface ListComposerProps {
  boardId: string;
  /** The list immediately LEFT of the gap this composer fills; null = the far left end. */
  previousListId: string | null;
  /** The list immediately RIGHT of the gap; null = the far right end (the ordinary case). */
  nextListId: string | null;
  /** Controlled: the strip owns which composer is open, so only ever one is. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 'button' renders the `Add a list` trigger when closed. 'none' renders the form alone — the gap composer. */
  trigger?: 'button' | 'none';
  className?: string;
}

/**
 * `TextField`'s own rule: a counter earns its place only once it has
 * something to say. 512 is the wire limit (`LIST_TITLE_MAX_LENGTH`); a
 * counter that showed for every two-word list name would shout on the
 * ordinary case, so it stays hidden until 50 characters remain.
 */
const TITLE_COUNTER_WARN_AT = LIST_TITLE_MAX_LENGTH - 50;

export function ListComposer({
  boardId,
  previousListId,
  nextListId,
  open,
  onOpenChange,
  trigger = 'button',
  className,
}: ListComposerProps): ReactElement {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const createList = useCreateList(boardId);
  // Above the `if (!open)` return, with the other hooks — the closed composer
  // needs `t` for its trigger and the open one for four more strings, and a
  // hook read after a conditional return is not a hook.
  const { t } = useInterfaceLanguage();

  // Fires on every entry point that can set `open` to true — the trigger
  // button, a gap double-click, and the header menu's keyboard equivalent —
  // because none of those live in this file and none of them can focus the
  // field themselves.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();

    /*
     * ⚠ And again on the next frame, which is not belt-and-braces.
     *
     * One of the three entry points is a `Menu` item (`Add a list to the
     * right`, `FR-BOARD-009` AC3's keyboard path). Radix's `DropdownMenu` runs
     * its own focus restoration as it closes, AFTER this component has mounted
     * and focused — so the synchronous call above wins the render and loses
     * the frame, and the member ends up typing into nothing. `Menu` now
     * exposes `onCloseAutoFocus` and `ListHeader` cancels the restoration for
     * that item, which stops focus being pulled back to the trigger; this
     * second call is what actually puts it in the field afterwards.
     *
     * Measured, not assumed: `FR-BOARD-009`'s AC3-keyboard spec fails without
     * it and passes with it. The rAF is cancelled on unmount so a composer
     * closed within the frame cannot steal focus on its way out.
     */
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  if (!open) {
    // 'none': the caller's own gap element is the trigger — this file
    // renders nothing until `open` says otherwise. An empty fragment, not
    // `null`, so the export keeps its fixed `ReactElement` return type
    // (brief §6.3) exactly, for the other agents compiling against it.
    if (trigger === 'none') return <></>;
    return (
      <Button
        variant="secondary"
        className={cn('w-full justify-start', className)}
        onClick={() => onOpenChange(true)}
      >
        {t('boardFace.list.add')}
      </Button>
    );
  }

  const trimmedTitle = title.trim();
  const canSubmit = trimmedTitle.length > 0;

  function closeWithoutCreating() {
    setTitle('');
    onOpenChange(false);
  }

  // AC2: Escape closes without creating. Attached to the form rather than
  // just the field so it also fires with focus on the submit or cancel
  // button.
  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    closeWithoutCreating();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // The edge case: whitespace-only is refused client-side, before the
    // request — the 422 is the backstop, not the mechanism (brief §3.3).
    if (!canSubmit) return;

    createList.mutate({
      title: trimmedTitle,
      previousListId,
      nextListId,
    });
    // The new column may be off-screen to the right of the strip.
    announce(t('boardFace.list.added', { title: trimmedTitle }));

    // AC1: stays open and empty, focus retained.
    setTitle('');
    inputRef.current?.focus();
  }

  return (
    <ListPanel className={cn('w-full', className)}>
      <form
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="flex flex-col gap-2"
      >
        <TextField
          ref={inputRef}
          label={t('boardFace.list.composer.name')}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={LIST_TITLE_MAX_LENGTH}
          counter={{
            value: title.length,
            max: LIST_TITLE_MAX_LENGTH,
            warnAt: TITLE_COUNTER_WARN_AT,
          }}
        />
        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
            {t('boardFace.list.composer.submit')}
          </Button>
          <Button type="button" variant="quiet" size="sm" onClick={closeWithoutCreating}>
            {t('boardFace.list.composer.cancel')}
          </Button>
        </div>
      </form>
    </ListPanel>
  );
}
